import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

const pool = () => {
  const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
  if (!userPoolId || !clientId) throw new Error("Authentication is not configured for this environment.");
  return new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });
};

const emailOf = (email: string) => email.trim().toLowerCase();
const user = (email: string) => new CognitoUser({ Username: emailOf(email), Pool: pool() });
const message = (error: unknown) => {
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
  if (code === "UsernameExistsException") return "An account may already exist for this email. Try signing in or resetting your password.";
  if (code === "NotAuthorizedException") return "The email or password is incorrect.";
  if (code === "UserNotConfirmedException") return "Confirm your email address before signing in.";
  return error instanceof Error ? error.message : "Authentication failed.";
};

export const authError = message;
export type SignInResult =
  | { status: "authenticated"; session: CognitoUserSession }
  | { status: "otp"; user: CognitoUser }
  | { status: "newPassword"; user: CognitoUser };

const authenticationCallbacks = (cognitoUser: CognitoUser, resolve: (value: SignInResult) => void, reject: (reason: Error) => void) => ({
  onSuccess: (session: CognitoUserSession) => resolve({ status: "authenticated", session }),
  onFailure: (error: unknown) => reject(new Error(message(error))),
  customChallenge: () => resolve({ status: "otp", user: cognitoUser }),
  mfaRequired: () => resolve({ status: "otp", user: cognitoUser }),
  newPasswordRequired: () => resolve({ status: "newPassword", user: cognitoUser }),
});

export const signIn = (email: string, password: string) => new Promise<SignInResult>((resolve, reject) => {
  const cognitoUser = user(email);
  cognitoUser.setAuthenticationFlowType("CUSTOM_AUTH");
  cognitoUser.authenticateUser(new AuthenticationDetails({ Username: emailOf(email), Password: password }), authenticationCallbacks(cognitoUser, resolve, reject));
});
export const signInAfterConfirmation = (email: string, password: string) => new Promise<CognitoUserSession>((resolve, reject) => {
  const cognitoUser = user(email);
  cognitoUser.authenticateUser(new AuthenticationDetails({ Username: emailOf(email), Password: password }), {
    onSuccess: resolve,
    onFailure: (error: unknown) => reject(new Error(message(error))),
  });
});
export const answerOtp = (cognitoUser: CognitoUser, code: string) => new Promise<CognitoUserSession>((resolve, reject) => {
  cognitoUser.sendCustomChallengeAnswer(code.trim(), { onSuccess: resolve, onFailure: (error) => reject(new Error(message(error))) });
});
export const completeNewPassword = (cognitoUser: CognitoUser, password: string) => new Promise<SignInResult>((resolve, reject) => {
  cognitoUser.completeNewPasswordChallenge(password, {}, authenticationCallbacks(cognitoUser, resolve, reject));
});
export const signUp = (email: string, password: string) => new Promise<void>((resolve, reject) => {
  pool().signUp(emailOf(email), password, [new CognitoUserAttribute({ Name: "email", Value: emailOf(email) })], [], (error) => error ? reject(new Error(message(error))) : resolve());
});
export const confirmRegistration = (email: string, code: string) => new Promise<void>((resolve, reject) => user(email).confirmRegistration(code.trim(), true, (error) => error ? reject(new Error(message(error))) : resolve()));
export const resendConfirmationCode = (email: string) => new Promise<void>((resolve, reject) => user(email).resendConfirmationCode((error) => error ? reject(new Error(message(error))) : resolve()));
export const forgotPassword = (email: string) => new Promise<void>((resolve, reject) => user(email).forgotPassword({ onSuccess: () => resolve(), onFailure: (error) => reject(new Error(message(error))) }));
export const confirmPassword = (email: string, code: string, password: string) => new Promise<void>((resolve, reject) => user(email).confirmPassword(code.trim(), password, { onSuccess: () => resolve(), onFailure: (error) => reject(new Error(message(error))) }));
export const currentUser = () => pool().getCurrentUser();
export const currentSession = () => new Promise<CognitoUserSession | null>((resolve) => {
  const current = currentUser();
  if (!current) return resolve(null);
  current.getSession((error: Error | null, session: CognitoUserSession | null) => resolve(error || !session || !session.isValid() ? null : session));
});
export const signOut = () => currentUser()?.signOut();
